import {FetchError} from './ServerError'
import {
  HttpMethods,
  NetworkHandler,
  NetworkInterface,
  NetworkOptions,
  ResponseBody,
  ResponseHeaders,
  ResponseStatus,
  ResponseText,
  Url,
} from './types'
import {getFetchErrorMessage, isBlob, prepareFetchHeaders, serializeFetchBody} from './utils'

type ExecuteCallback = (
  error: any,
  status: ResponseStatus,
  responseBody?: ResponseBody,
  responseText?: ResponseText,
  responseHeaders?: ResponseHeaders,
) => void

export const fetchNetworkInterface: NetworkInterface = (
  url: Url,
  method: HttpMethods,
  {body, headers, credentials}: NetworkOptions = {},
): NetworkHandler => {
  const options: RequestInit = {method, headers: prepareFetchHeaders(headers, body)}

  if (body) {
    options.body = serializeFetchBody(body)
  }

  if (credentials === 'include') {
    options.credentials = 'include'
  }

  let controller: AbortController | null = new AbortController()
  options.signal = controller.signal

  const execute = async (cb: ExecuteCallback) => {
    try {
      const response = await globalThis.fetch(url, options)
      const resStatus = response.status
      const resHeaders: ResponseHeaders = Object.fromEntries(response.headers)
      let resBody: ResponseBody
      let resText: ResponseText | undefined

      if (isBlob(resHeaders['content-type'])) {
        resBody = await response.blob()
      } else {
        try {
          resBody = await response.json()
        } catch {
          if (!response.bodyUsed) {
            resText = await response.text()
          }
        }
      }

      if (!response.ok) {
        const errorMessage = getFetchErrorMessage({url, status: resStatus, body: resBody})
        const error = new FetchError(errorMessage, {text: resBody || resText})

        cb(error, resStatus, resBody, resText, resHeaders)
        return
      }

      cb(null, resStatus, resBody, resText, resHeaders)
    } catch (error) {
      cb(error, 0, undefined, undefined, undefined)
    }
  }

  const abort = () => {
    if (controller) {
      controller.abort()
      controller = null
    }
  }

  return {
    abort,
    execute,
  }
}
